import React, { useContext, useEffect, useRef } from 'react';
import { I18nextContext } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import { PageSlice, TrustpilotTemplateId } from '@contracts';
import locales from '../../../i18n';
import LinkTranslated from './LinkTranslated';
import useDesktop from './useDesktop';

interface Props {
  slice?: PageSlice;
  justifyContent?: string;
  showFallback?: boolean;
}

const Trustpilot: React.FC<Props> = ({ slice, justifyContent = 'center', showFallback = true }) => {
  const backgroundColor = slice?.primary?.background_color ?? '';
  const color = slice?.primary?.text_color ?? Colors.forestGreen;
  const type = slice?.primary?.type ?? 'basic';
  const tags = slice?.primary?.tags?.text ?? 'SelectedReview';
  const mainQuote = slice?.primary?.main_quote?.text;
  const width = '100%';
  const languageContext = useContext(I18nextContext);
  const currentLanguage = locales.find((locale) => locale.path === languageContext.language);
  const ref = useRef(null);
  const isDesktop = useDesktop(1024);
  let templateId = TrustpilotTemplateId.Basic;
  let height = 28;
  if (type === 'reviews-carousel') {
    templateId = TrustpilotTemplateId.ReviewsCarousel;
    height = 140;
  } else if (type === 'slider') {
    templateId = TrustpilotTemplateId.Slider;
    height = 250;
  }

  useEffect(() => {
    if (window && window.Trustpilot) {
      window.Trustpilot.loadFromElement(ref.current, true);
    }
  }, []);

  return (
    <>
      <Container $backgroundColor={backgroundColor} className="trustpilot-container">
        {mainQuote && <Title dangerouslySetInnerHTML={{ __html: mainQuote }} />}
        <WidgetContainer
          ref={ref}
          className="trustpilot-widget"
          data-locale={currentLanguage?.locale}
          data-template-id={templateId}
          data-businessunit-id="5f6f179ecb18f8000171278b"
          data-style-height={`${height}px`}
          data-style-width={width}
          data-theme="light"
          data-stars="5"
          data-tags={tags}
          data-no-reviews="hide"
          data-scroll-to-list="true"
          data-allow-robots="true"
          data-text-color={color}
          style={{ display: 'flex', justifyContent: isDesktop ? justifyContent : 'center' }}
        >
          {showFallback && (
            <FallbackLink to="https://www.trustpilot.com/review/yourdaye.com" targetBlank>
              {' '}
              See our reviews on Trustpilot
            </FallbackLink>
          )}
        </WidgetContainer>
      </Container>
    </>
  );
};
export default Trustpilot;

interface ContainerProps {
  $backgroundColor?: string;
}

const Container = styled.div<ContainerProps>`
  pointer-events: none;

  background-color: ${(props) => (props.$backgroundColor ? props.$backgroundColor : 'transparent')};
  padding: ${(props) => (props.$backgroundColor ? '60px 0' : '0')};

  @media (max-width: 1024px) {
    padding: 20px 0;
  }
`;

const Title = styled.div`
  font-family: 'Domaine';
  font-style: normal;
  font-weight: normal;
  font-size: 50px;
  line-height: 110%;
  text-align: center;
  letter-spacing: -0.01em;
  margin-bottom: 60px;
  width: 100%;
  color: ${Colors.navyBlue};

  @media (max-width: 1024px) {
    font-size: 24px;
    line-height: 120%;
    padding: 20px;
    justify-content: center;
    margin-bottom: 10px;
  }
`;

const WidgetContainer = styled.div`
  text-align: center;
  max-width: 1200px;
  margin: 0 auto;
`;

const FallbackLink = styled(LinkTranslated)`
  font-family: 'BrownStd';
  font-size: 14px;
  padding-left: 45px;
  color: ${Colors.navyBlue};
  opacity: 0.65;
`;
